import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import HealingMetricsPanel from './HealingMetricsPanel';

function toInputDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatDisplayDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 29);
  return { from: toInputDate(from), to: toInputDate(to) };
}

const PRESETS = [
  { id: '7d', label: '7d', days: 7 },
  { id: '30d', label: '30d', days: 30 },
  { id: '90d', label: '90d', days: 90 },
];

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

function Panel({ title, hint, children, sx }) {
  return (
    <Box
      sx={{
        background: '#1e293b',
        border: '1px solid #243044',
        borderRadius: '8px',
        px: 2.5,
        py: 2.25,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#e2e8f0' }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: '0.6875rem', color: '#64748b' }}>{hint}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <Box
      sx={{
        background: '#1e293b',
        px: 2.5,
        py: 2.25,
        minHeight: 108,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: '#64748b',
          mb: 1.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: '1.75rem',
          fontWeight: 500,
          letterSpacing: '-0.02em',
          lineHeight: 1,
          color: '#e2e8f0',
        }}
      >
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {hint && (
        <Typography sx={{ mt: 1.25, fontSize: '0.75rem', color: '#64748b' }}>{hint}</Typography>
      )}
    </Box>
  );
}

function ClassRow({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '140px 1fr 64px',
        alignItems: 'center',
        gap: 1.5,
        py: 1.35,
        borderBottom: '1px solid #243044',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{label}</Typography>
      </Box>
      <Box sx={{ height: 6, background: '#0f172a', borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 1 }} />
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right', color: '#e2e8f0' }}>
        {count.toLocaleString()}
      </Typography>
    </Box>
  );
}

function formatRate(value, suffix = '%') {
  if (value === null || value === undefined) return '—';
  return `${value}${suffix}`;
}

function DailyVolumeChart({ points }) {
  const width = 640;
  const height = 160;
  const padY = 8;
  const maxVal = Math.max(1, ...points.flatMap((p) => [p.reported, p.classified, p.fixed]));

  const toX = (i) => (points.length <= 1 ? 0 : (i / (points.length - 1)) * width);
  const toY = (v) => height - padY - ((v / maxVal) * (height - padY * 2));

  const linePath = (key) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p[key])}`).join(' ');

  const areaPath = () => {
    if (!points.length) return '';
    const top = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.reported)}`).join(' ');
    return `${top} L${toX(points.length - 1)},${height} L0,${height} Z`;
  };

  const mid = points[Math.floor(points.length / 2)];

  return (
    <Box>
      <Box sx={{ height: 160, width: '100%' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none">
          <line x1="0" y1={toY(maxVal * 0.75)} x2={width} y2={toY(maxVal * 0.75)} stroke="#243044" strokeWidth="1" />
          <line x1="0" y1={toY(maxVal * 0.5)} x2={width} y2={toY(maxVal * 0.5)} stroke="#243044" strokeWidth="1" />
          <line x1="0" y1={toY(maxVal * 0.25)} x2={width} y2={toY(maxVal * 0.25)} stroke="#243044" strokeWidth="1" />
          <path d={areaPath()} fill="rgba(74,127,212,0.08)" />
          <path d={linePath('reported')} fill="none" stroke="#4a7fd4" strokeWidth="1.75" />
          <path d={linePath('classified')} fill="none" stroke="#3d9b74" strokeWidth="1.75" />
          <path d={linePath('fixed')} fill="none" stroke="#c4923a" strokeWidth="1.5" strokeDasharray="4 3" />
        </svg>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: '#64748b' }}>
          {formatShortDate(points[0]?.date)}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: '#64748b' }}>
          {formatShortDate(mid?.date)}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 10, color: '#64748b' }}>
          {formatShortDate(points[points.length - 1]?.date)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, fontSize: '0.6875rem', color: '#64748b' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: '#4a7fd4' }} /> Reported
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: '#3d9b74' }} /> Classified
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: '#c4923a' }} /> Fixed
        </Box>
      </Box>
    </Box>
  );
}

function MetricTable({ columns, rows, empty }) {
  if (!rows?.length) {
    return <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{empty || 'No data'}</Typography>;
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
        <Box component="thead">
          <Box component="tr">
            {columns.map((col) => (
              <Box
                component="th"
                key={col.key}
                sx={{
                  textAlign: col.align || 'left',
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#64748b',
                  pb: 1.25,
                  borderBottom: '1px solid #243044',
                  pr: col.align === 'right' ? 0 : 1.5,
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, idx) => (
            <Box component="tr" key={idx}>
              {columns.map((col) => (
                <Box
                  component="td"
                  key={col.key}
                  sx={{
                    py: 1.35,
                    pr: col.align === 'right' ? 0 : 1.5,
                    borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #243044',
                    textAlign: col.align || 'left',
                    color: col.primary ? '#e2e8f0' : '#94a3b8',
                    fontWeight: col.primary ? 500 : 400,
                    fontFamily: col.mono ? MONO : 'inherit',
                    fontSize: col.mono ? '0.75rem' : 'inherit',
                  }}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function RateRow({ label, sub, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        pb: 1.5,
        mb: 1.5,
        borderBottom: '1px solid #243044',
        '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 },
      }}
    >
      <Box>
        <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>{label}</Typography>
        {sub && <Typography sx={{ fontSize: '0.6875rem', color: '#64748b', mt: 0.25 }}>{sub}</Typography>}
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.875rem', fontWeight: 500, color: '#e2e8f0' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function MetricsTab() {
  const { isAdmin } = useAuth();
  const initial = useMemo(() => defaultRange(), []);
  const [metricsMode, setMetricsMode] = useState('anomaly'); // anomaly | healing
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [activePreset, setActivePreset] = useState('30d');
  const [metrics, setMetrics] = useState(null);
  const [healingMetrics, setHealingMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isHealing = metricsMode === 'healing';

  useEffect(() => {
    if (!isAdmin && metricsMode === 'healing') {
      setMetricsMode('anomaly');
    }
  }, [isAdmin, metricsMode]);

  const loadAnomalyMetrics = useCallback(async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getMetrics(from, to);
      setMetrics(data);
    } catch (err) {
      setMetrics(null);
      setError(err.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHealingMetrics = useCallback(async (from, to) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getHealingMetrics(from, to);
      setHealingMetrics(data);
    } catch (err) {
      setHealingMetrics(null);
      setError(err.message || 'Failed to load healing metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadForMode = useCallback((mode, from, to) => {
    if (mode === 'healing') return loadHealingMetrics(from, to);
    return loadAnomalyMetrics(from, to);
  }, [loadAnomalyMetrics, loadHealingMetrics]);

  useEffect(() => {
    loadForMode(metricsMode, dateFrom, dateTo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial load only

  const switchMode = (mode) => {
    if (mode === metricsMode) return;
    if (mode === 'healing' && !isAdmin) return;
    setMetricsMode(mode);
    loadForMode(mode, dateFrom, dateTo);
  };

  const applyPreset = (preset) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - preset.days + 1);
    const fromStr = toInputDate(from);
    const toStr = toInputDate(to);
    setDateFrom(fromStr);
    setDateTo(toStr);
    setActivePreset(preset.id);
    loadForMode(metricsMode, fromStr, toStr);
  };

  const handleApply = () => {
    if (!dateFrom || !dateTo) {
      setSnackbar({ open: true, message: 'Select both from and to dates', severity: 'warning' });
      return;
    }
    if (dateTo < dateFrom) {
      setSnackbar({ open: true, message: 'End date must be on or after start date', severity: 'warning' });
      return;
    }
    setActivePreset('custom');
    loadForMode(metricsMode, dateFrom, dateTo);
  };

  const handleDownload = async () => {
    if (isHealing) {
      setSnackbar({ open: true, message: 'CSV export for healing metrics coming soon', severity: 'info' });
      return;
    }
    try {
      await api.downloadMetricsCsv(dateFrom, dateTo);
      setSnackbar({ open: true, message: 'Summary CSV downloaded', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Download failed', severity: 'error' });
    }
  };

  const reported = metrics?.anomalies_reported ?? 0;
  const classified = metrics?.classified ?? {
    all: 0,
    true_anomaly: 0,
    false_positive: 0,
    reviewed: 0,
  };
  const rates = metrics?.rates || {};
  const dailyVolume = metrics?.daily_volume || [];
  const opacity = loading ? 0.6 : 1;
  const activeData = isHealing ? healingMetrics : metrics;

  return (
    <Box>
      {isAdmin && (
        <Box
          sx={{
            display: 'inline-flex',
            border: '1px solid #243044',
            borderRadius: '6px',
            overflow: 'hidden',
            background: '#1e293b',
            mb: 2,
          }}
        >
          {[
            { id: 'anomaly', label: 'Anomaly metrics' },
            { id: 'healing', label: 'Healing metrics' },
          ].map((tab, idx) => (
            <Box
              key={tab.id}
              component="button"
              type="button"
              onClick={() => switchMode(tab.id)}
              sx={{
                appearance: 'none',
                border: 'none',
                borderRight: idx === 0 ? '1px solid #243044' : 'none',
                background: metricsMode === tab.id ? '#162033' : 'transparent',
                color: metricsMode === tab.id ? '#e2e8f0' : '#94a3b8',
                fontSize: '0.8125rem',
                fontWeight: metricsMode === tab.id ? 600 : 500,
                px: 2,
                py: 1,
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:hover': { color: '#e2e8f0', background: '#162033' },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2.5,
          pb: 2.5,
          borderBottom: '1px solid #243044',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Box
            sx={{
              display: 'flex',
              border: '1px solid #243044',
              borderRadius: '6px',
              overflow: 'hidden',
              background: '#1e293b',
            }}
          >
            {PRESETS.map((p, idx) => (
              <Box
                key={p.id}
                component="button"
                type="button"
                onClick={() => applyPreset(p)}
                sx={{
                  appearance: 'none',
                  border: 'none',
                  borderRight: idx < PRESETS.length - 1 ? '1px solid #243044' : 'none',
                  background: activePreset === p.id ? '#162033' : 'transparent',
                  color: activePreset === p.id ? '#e2e8f0' : '#94a3b8',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.9,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  '&:hover': { color: '#e2e8f0', background: '#162033' },
                }}
              >
                {p.label}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.6,
              border: '1px solid #243044',
              borderRadius: '6px',
              background: '#1e293b',
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: '#64748b' }} />
            <Typography
              component="label"
              htmlFor="metrics-from"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#64748b',
              }}
            >
              From
            </Typography>
            <Box
              component="input"
              id="metrics-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setActivePreset('custom');
              }}
              sx={{
                border: 'none',
                background: 'transparent',
                color: '#e2e8f0',
                fontFamily: MONO,
                fontSize: '0.8125rem',
                colorScheme: 'dark',
                outline: 'none',
              }}
            />
            <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>→</Typography>
            <Typography
              component="label"
              htmlFor="metrics-to"
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#64748b',
              }}
            >
              To
            </Typography>
            <Box
              component="input"
              id="metrics-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setActivePreset('custom');
              }}
              sx={{
                border: 'none',
                background: 'transparent',
                color: '#e2e8f0',
                fontFamily: MONO,
                fontSize: '0.8125rem',
                colorScheme: 'dark',
                outline: 'none',
              }}
            />
          </Box>

          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.75rem',
              background: '#2a4a7a',
              boxShadow: 'none',
              '&:hover': { background: '#335890', boxShadow: 'none' },
            }}
          >
            Apply
          </Button>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={handleDownload}
          disabled={!activeData || isHealing}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.75rem',
            color: '#cbd5e1',
            borderColor: '#334155',
            '&:hover': { borderColor: '#475569', background: 'rgba(255,255,255,0.03)' },
          }}
        >
          Download CSV
        </Button>
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 2.5 }}>
        Showing{' '}
        <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500 }}>
          {formatDisplayDate(
            (isHealing ? healingMetrics?.date_from : metrics?.date_from) || dateFrom,
          )}{' '}
          –{' '}
          {formatDisplayDate(
            (isHealing ? healingMetrics?.date_to : metrics?.date_to) || dateTo,
          )}
        </Box>
        {isHealing ? (
          <>
            {' · '}Heal jobs by <code style={{ color: '#94a3b8' }}>created_at</code>
            {' · '}Path A = Edit Config modal
            {' · '}Path B / merge via GitHub webhook fields on{' '}
            <code style={{ color: '#94a3b8' }}>review_context</code>
          </>
        ) : (
          <>
            {' · '}Reported = system <code style={{ color: '#94a3b8' }}>TRUE_ANOMALY</code>
            {' · '}uses <code style={{ color: '#94a3b8' }}>last_seen_at</code>
            {' · '}Classified / true anomalies use <code style={{ color: '#94a3b8' }}>classified_at</code>
            {' · '}Fixed uses <code style={{ color: '#94a3b8' }}>fixed_at</code>
          </>
        )}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !activeData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : isHealing ? (
        <Box sx={{ opacity, transition: 'opacity 0.15s' }}>
          <HealingMetricsPanel metrics={healingMetrics} />
        </Box>
      ) : (
        <Box sx={{ opacity, transition: 'opacity 0.15s' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: '1px',
              background: '#243044',
              border: '1px solid #243044',
              borderRadius: '8px',
              overflow: 'hidden',
              mb: 2,
            }}
          >
            <KpiCard
              label="Anomalies reported"
              value={reported}
              hint="System TRUE_ANOMALY · last_seen_at in range"
            />
            <KpiCard
              label="True anomalies"
              value={metrics?.true_anomalies ?? 0}
              hint={
                classified.all > 0
                  ? `${Math.round(((metrics?.true_anomalies ?? 0) / classified.all) * 100)}% of classified · classified_at in range`
                  : 'classified_at in range · review_status true_anomaly'
              }
            />
            <KpiCard
              label="Classified by users"
              value={classified.all}
              hint="classified_at in range"
            />
            <KpiCard label="Anomalies fixed" value={metrics?.anomalies_fixed ?? 0} hint="fixed_at in range" />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <Panel title="Classification breakdown" hint="By review_status · classified_at in range">
              <ClassRow label="All classified" count={classified.all} total={classified.all || 1} color="#4a7fd4" />
              <ClassRow label="True anomaly" count={classified.true_anomaly} total={classified.all || 1} color="#3d9b74" />
              <ClassRow label="False positive" count={classified.false_positive} total={classified.all || 1} color="#c45c5c" />
              <ClassRow label="Reviewed" count={classified.reviewed} total={classified.all || 1} color="#c4923a" />
            </Panel>

            <Panel title="Rates & throughput">
              <RateRow label="Review completion" sub="Classified ÷ reported" value={formatRate(rates.review_completion_pct)} />
              <RateRow label="True-anomaly rate" sub="Among classified" value={formatRate(rates.true_anomaly_rate_pct)} />
              <RateRow label="Fix rate" sub="Fixed ÷ true anomalies" value={formatRate(rates.fix_rate_pct)} />
              <RateRow
                label="Median time to classify"
                sub="first_seen_at → classified_at"
                value={formatRate(rates.median_time_to_classify_days, 'd')}
              />
              <RateRow
                label="Median time to fix"
                sub="classified_at → fixed_at"
                value={formatRate(rates.median_time_to_fix_days, 'd')}
              />
            </Panel>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <Panel title="Daily volume" hint="Reported = TRUE_ANOMALY occurrences by occurred_at">
              {dailyVolume.length > 0 ? (
                <DailyVolumeChart points={dailyVolume} />
              ) : (
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>No daily data</Typography>
              )}
            </Panel>

            <Panel title="Classification mix" hint="Of classified_at in range">
              <ClassRow
                label="True anomaly"
                count={classified.true_anomaly}
                total={classified.all || 1}
                color="#3d9b74"
              />
              <ClassRow
                label="False positive"
                count={classified.false_positive}
                total={classified.all || 1}
                color="#c45c5c"
              />
              <ClassRow
                label="Reviewed"
                count={classified.reviewed}
                total={classified.all || 1}
                color="#c4923a"
              />
              <Typography sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #243044', fontSize: '0.75rem', color: '#64748b' }}>
                Unreviewed (last_seen in range):{' '}
                <Box component="span" sx={{ fontFamily: MONO, color: '#e2e8f0' }}>
                  {(metrics?.unreviewed ?? 0).toLocaleString()}
                </Box>
                {reported > 0 ? ` (${Math.round(((metrics?.unreviewed ?? 0) / reported) * 100)}%)` : ''}
              </Typography>
            </Panel>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <Panel title="By retailer" hint="Top 5 · reported">
              <MetricTable
                columns={[
                  { key: 'retailer', label: 'Retailer', primary: true },
                  { key: 'reported', label: 'Reported', align: 'right', mono: true },
                  { key: 'fixed', label: 'Fixed', align: 'right', mono: true },
                ]}
                rows={metrics?.by_retailer || []}
              />
            </Panel>

            <Panel title="By attribute" hint="Top 5 · classified_at in range">
              <MetricTable
                columns={[
                  { key: 'attribute', label: 'Attribute', primary: true },
                  { key: 'true_anomaly', label: 'TA', align: 'right', mono: true },
                  { key: 'false_positive', label: 'FP', align: 'right', mono: true },
                ]}
                rows={metrics?.by_attribute || []}
              />
            </Panel>

            <Panel title="Reviewer activity" hint="classified_at in range">
              <MetricTable
                columns={[
                  { key: 'reviewer', label: 'Reviewer', primary: true },
                  { key: 'done', label: 'Done', align: 'right', mono: true },
                  {
                    key: 'true_anomaly_pct',
                    label: 'TA%',
                    align: 'right',
                    mono: true,
                    render: (row) =>
                      row.true_anomaly_pct === null || row.true_anomaly_pct === undefined
                        ? '—'
                        : `${Math.round(row.true_anomaly_pct)}%`,
                  },
                ]}
                rows={metrics?.by_reviewer || []}
              />
            </Panel>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
